import { getAllUsers, updateAdminUser, updateAdminUserStatus, deleteAdminUser, createAdminUser, bulkDeleteUsersApi } from "../../helpers/apis";
import {
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFailure,
  deleteUserSuccess,
  bulkDeleteUsersSuccess,
  updateUserSuccess,
  addUserSuccess
} from "./reducer";

export const fetchAllUsers = (params = {}) => async (dispatch) => {
  dispatch(fetchUsersStart());
  try {
    const backendParams = {
      page: params.page || params.currentPage || 1,
      limit: params.perPage || params.limit || 10,
    };

    const statusFilter = params.statusFilter || params.status || "all";
    backendParams.status = statusFilter;

    if (params.search && params.search.trim() !== "") {
      backendParams.search = params.search.trim();
    }

    if (params.orderBy) {
      const fieldMapping = {
        createdAt: "createdAt",
        created_at: "createdAt",
        updatedAt: "updatedAt",
        updated_at: "updatedAt",
        firstname: "firstname",
        lastname: "lastname",
        email: "email",
      };

      let fieldName = params.orderBy;
      if (typeof params.orderBy === "function") {
        const match = params.orderBy.toString().match(/row\.(\w+)/);
        if (match) fieldName = match[1];
      } else if (typeof params.orderBy === "string") {
        fieldName = params.orderBy.replace("row => row.", "");
      }

      backendParams.orderBy = fieldMapping[fieldName] || "createdAt";
    } else {
      backendParams.orderBy = "createdAt";
    }

    backendParams.order =
      params.order?.toLowerCase() === "asc" ? "asc" : "desc";

    const response = await getAllUsers(backendParams);

    if (response && response.status === 1) {
      const users = response.data?.users || [];
      const totalRows = response.totalData || response.totalItems || response.data?.pagination?.totalItems || users.length;

      // ✅ Normalize date fields
      const normalizedUsers = users.map(user => ({
        ...user,
        createdAt: user.createdAt || user.created_at,
        updatedAt: user.updatedAt || user.updated_at,
      }));

      dispatch(fetchUsersSuccess({ users: normalizedUsers, totalRows }));

      return { ok: true, data: { users: normalizedUsers, totalRows } };
    } else {
      dispatch(fetchUsersFailure(response?.message || "Failed to fetch users"));
      return { ok: false, message: response?.message || "Failed to fetch users" };
    }
  } catch (error) {
    console.error("Users Error:", error);
    dispatch(fetchUsersFailure(error.message || "Failed to fetch users"));
    return { ok: false, message: error.message || "Failed to fetch users" };
  }
};

// ✅ Optimized delete - no refetch needed
export const deleteUserById = (id) => async (dispatch) => {
  try {
    const response = await deleteAdminUser(id);

    if (response?.status === 1) {
      dispatch(updateUserSuccess({
        _id: id,
        isDeleted: true,
        deletedAt: new Date().toISOString(),
        status: "deleted" // ✅ Update status to deleted
      }));

      return {
        ok: true,
        message: response.message || "User soft deleted successfully",
        deletedPosts: response?.data?.deletedPosts ?? response?.deletedPosts,
      };
    } else {
      return {
        ok: false,
        message: response?.message || "Failed to soft delete user"
      };
    }
  } catch (error) {
    console.error("Soft Delete User Error:", error);
    return { ok: false, message: error.message || "Failed to soft delete user" };
  }
};

export const updateUserById = (id, data) => async (dispatch) => {
  try {
    const response = await updateAdminUser(id, data);
    if (response?.status === 1) {
      // ✅ Include status in update
      dispatch(updateUserSuccess({
        ...data,
        _id: id,
        updatedAt: new Date().toISOString()
      }));
      return {
        ok: true,
        message: response.message || "User updated successfully"
      };
    } else {
      return {
        ok: false,
        message: response?.message || "Failed to update user"
      };
    }
  } catch (error) {
    return { ok: false, message: error.message };
  }
};

// ✅ Optimized status update - update local state instead of refetching
export const updateUserStatusById = (id, status) => async (dispatch) => {
  try {
    const response = await updateAdminUserStatus(id, { status });
    if (response?.status === 1) {
      // ✅ Update local state instead of refetching
      dispatch(updateUserSuccess({
        _id: id,
        status: status.status || status,
        updatedAt: new Date().toISOString()
      }));

      return {
        ok: true,
        message: response.message || `Status updated to ${status.status || status}`
      };
    } else {
      return {
        ok: false,
        message: response?.message || "Failed to update status"
      };
    }
  } catch (error) {
    return { ok: false, message: error.message };
  }
};

export const createUser = (data) => async (dispatch) => {
  try {
    // ✅ Ensure new users have active status and createdAt
    const userData = {
      ...data,
      status: data.status || "active",
      createdAt: new Date().toISOString(),
    };

    const response = await createAdminUser(userData);
    if (response?.status === 1) {
      dispatch(addUserSuccess({
        ...response.data,
        createdAt: response.data.createdAt || new Date().toISOString(),
        status: response.data.status || "active"
      }));
      return {
        ok: true,
        message: response.message || "User created successfully"
      };
    } else {
      return {
        ok: false,
        message: response?.message || "Failed to create user"
      };
    }
  } catch (error) {
    console.error("Create User Error:", error);
    return { ok: false, message: error.message };
  }
};

// ✅ Optimized bulk delete
export const bulkDeleteUsers = (userIds) => async (dispatch) => {
  try {
    const response = await bulkDeleteUsersApi({ userIds, softDelete: true });

    if (response?.status === 1) {
      const deletedAt = new Date().toISOString();
      userIds.forEach((id) => {
        dispatch(updateUserSuccess({
          _id: id,
          isDeleted: true,
          deletedAt,
          status: "deleted" // ✅ Update status to deleted
        }));
      });

      return {
        ok: true,
        message: response.message || `Soft deleted ${response.data?.deletedUsers || userIds.length} users successfully`,
        deletedCount: response.data?.deletedUsers || 0,
        deletedPosts: response.data?.deletedPosts ?? response.deletedPosts ?? 0
      };
    } else {
      return {
        ok: false,
        message: response?.message || "Failed to soft delete users"
      };
    }
  } catch (error) {
    console.error("Bulk Soft Delete Users Error:", error);
    return {
      ok: false,
      message: error.message || "Failed to soft delete users"
    };
  }
};